import type { RoutingSkuTargetRecord, SkuSupplyCandidate } from '@nadoutong/contracts';
import { LIVE_CAPABILITY_STATUSES } from '../../pages/capabilityStatus.ts';

/**
 * Row set for the merged supply panel (#1428).
 *
 * The panel shows ONE list assembled from two reads that describe the same
 * table (`routing_default_sku_targets`): the per-SKU target pool, and the
 * gateway's candidate view of the live-capability chain. The union — not the
 * intersection — is the row set, and that is the whole point of this module.
 *
 * On dev, 15 of 23 bound targets do not appear in the candidate list at all
 * (11 carry a null `capability_id`; 4 belong to SKUs whose candidate read
 * returns nothing). A panel built as "one row per candidate" would silently
 * drop those, including the two rows that are currently serving all of
 * `xai/grok-4.5`'s traffic. So every bound target renders, matched or not,
 * and an unmatched one carries the reason it could not be matched.
 */

/** Why a bound target has no candidate row to stand on. */
export type BoundRowIssue =
  /** `capability_id IS NULL` — pre-0086 row; the PUT gate refuses to save it. */
  | 'legacy_unpinned'
  /** Pinned, live-looking, but the candidate read did not return it. */
  | 'not_in_candidates'
  /** Pinned to a capability whose probe status is outside the live set. */
  | 'probe_abnormal';

export interface BoundSupplyRow {
  readonly target: RoutingSkuTargetRecord;
  /**
   * A candidate that backs this target, when the two reads agree. A legacy row
   * can match several (see `targetMatchesCandidate`); this is the first, used
   * only for display fallbacks — every match is consumed for grouping.
   */
  readonly candidate: SkuSupplyCandidate | null;
  readonly issue: BoundRowIssue | null;
}

export interface SupplyRowGroups {
  readonly bound: readonly BoundSupplyRow[];
  /** Unbound candidates whose upstream model equals the SKU's own model. */
  readonly recommended: readonly SkuSupplyCandidate[];
  /** Every other unbound candidate — a deliberate cross-model bind. */
  readonly crossModel: readonly SkuSupplyCandidate[];
}

/**
 * Transcription of the gateway's `bound` EXISTS predicate
 * (`internal-tenant-routing-supply-candidates.ts`): the physical four-tuple
 * must match, AND either the explicit `capability_id` pin matches, or — for a
 * legacy row that never got one — the canonical vendor/model pair does.
 *
 * Both branches are required. Matching on the pin column alone would report a
 * legacy-bound capability as free and invite a duplicate bind; matching on
 * vendor/model alone would collapse two capabilities of one four-tuple that
 * differ only by protocol (`ndt-pool-xai` vs `ndt-pool-xai-oai`) into one row.
 *
 * The legacy branch is deliberately NOT one-to-one. `upstream_model_capabilities`
 * is unique on `(upstream_id, account_id, key_bucket_id, channel_id, vendor,
 * model, protocol, upstream_model_name)`, so `(four-tuple, vendor, model)` can
 * select several capabilities that differ only by protocol or upstream model
 * name. The server evaluates its EXISTS once per candidate and therefore marks
 * ALL of them bound; callers here must consume every match rather than the
 * first, or the leftovers reappear with a bind button.
 *
 * The server additionally requires `enabled = 1`; this predicate does not. A
 * disabled target is still a row the operator owns and edits in the bound
 * group, so letting its capability ALSO reappear under "recommended" would
 * render the same identity twice with two different controls.
 */
export function targetMatchesCandidate(
  target: RoutingSkuTargetRecord,
  candidate: SkuSupplyCandidate,
): boolean {
  if (
    target.upstream_id !== candidate.upstream_id ||
    target.account_id !== candidate.account_id ||
    target.key_bucket_id !== candidate.key_bucket_id ||
    target.upstream_channel_id !== candidate.upstream_channel_id
  ) {
    return false;
  }
  return target.capability_id === null
    ? target.vendor === candidate.vendor && target.model === candidate.model
    : target.capability_id === candidate.capability_id;
}

function issueFor(
  target: RoutingSkuTargetRecord,
  candidate: SkuSupplyCandidate | null,
  candidatesLoaded: boolean,
): BoundRowIssue | null {
  if (target.capability_id === null) return 'legacy_unpinned';
  if (candidate !== null) return null;
  // A read that failed (or has not landed) says nothing about this pin.
  // Reporting "not in candidates" off an empty fallback array would turn a
  // transport error into a supply verdict and point the operator at
  // re-probing — or deleting — supply that routes fine.
  if (!candidatesLoaded) return null;
  // A pin the candidate read did not return is either dead supply (the probe
  // status says so) or a candidate read that answered for a different scope —
  // `xai/grok-4.5` returns zero candidates while two pins actively route.
  return LIVE_CAPABILITY_STATUSES.has(target.capability_status ?? '')
    ? 'not_in_candidates'
    : 'probe_abnormal';
}

export function groupSupplyRows({
  targets,
  candidates,
  candidatesLoaded,
  skuModel,
}: {
  readonly targets: readonly RoutingSkuTargetRecord[];
  readonly candidates: readonly SkuSupplyCandidate[];
  /** False while the candidate read is pending or failed — see `issueFor`. */
  readonly candidatesLoaded: boolean;
  /** The model the SKU SELLS — the recommendation test, never a candidate's. */
  readonly skuModel: string;
}): SupplyRowGroups {
  const matchedCapabilityIds = new Set<string>();
  const bound = targets.map((target): BoundSupplyRow => {
    const matches = candidates.filter((row) => targetMatchesCandidate(target, row));
    for (const match of matches) {
      matchedCapabilityIds.add(match.capability_id);
    }
    const candidate = matches[0] ?? null;
    return { target, candidate, issue: issueFor(target, candidate, candidatesLoaded) };
  });

  // `candidate.bound` is the server's own verdict over the same predicate, and
  // it is the floor here: whatever this client concludes, a candidate the
  // gateway already counts as bound never gets a bind button. Where the two
  // disagree — a match this code missed, a pool read that has not caught up —
  // the conservative side wins, because offering a duplicate bind performs a
  // write and hiding one does not.
  const unbound = candidates.filter(
    (candidate) => !candidate.bound && !matchedCapabilityIds.has(candidate.capability_id),
  );
  return {
    bound,
    recommended: unbound.filter((candidate) => candidate.model === skuModel),
    crossModel: unbound.filter((candidate) => candidate.model !== skuModel),
  };
}
